/* ---------------------------------------------------------------------------
   Capability detection.

   Mobile browsers are a v1 target, so the UI needs to know *before* a round
   starts whether this device can actually record — and to say something
   actionable when it can't. Feature-detect first; sniff the user agent only for
   the handful of quirks that cannot be feature-detected (iOS media policy).
   --------------------------------------------------------------------------- */

import { audioState, isAudioSupported, type AudioLifecycleState } from "./audio";
import { supportsVideoFrameCallback } from "./videoTiming";

/**
 * MediaRecorder mime types in order of preference.
 * Opus in WebM everywhere it exists; AAC in MP4 for Safari, which is the only
 * thing iOS will hand back.
 */
export const AUDIO_MIME_CANDIDATES = [
  "audio/webm;codecs=opus",
  "audio/webm",
  "audio/mp4;codecs=mp4a.40.2",
  "audio/mp4",
  "audio/ogg;codecs=opus",
] as const;

function ua(): string {
  return typeof navigator === "undefined" ? "" : navigator.userAgent;
}

export function isIOS(): boolean {
  if (typeof navigator === "undefined") return false;
  if (/iPad|iPhone|iPod/.test(ua())) return true;
  // iPadOS 13+ reports itself as a Mac; touch points give it away.
  return navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1;
}

export function isAndroid(): boolean {
  return /Android/i.test(ua());
}

export function isSafari(): boolean {
  const agent = ua();
  return /Safari/i.test(agent) && !/Chrome|Chromium|CriOS|FxiOS|Edg/i.test(agent);
}

export function isMobile(): boolean {
  if (typeof navigator === "undefined") return false;
  return isIOS() || isAndroid() || (navigator.maxTouchPoints > 1 && window.innerWidth < 900);
}

export function supportsMediaRecorder(): boolean {
  return typeof window !== "undefined" && typeof window.MediaRecorder === "function";
}

export function supportsGetUserMedia(): boolean {
  return (
    typeof navigator !== "undefined" &&
    typeof navigator.mediaDevices?.getUserMedia === "function"
  );
}

export function isSecureContextAvailable(): boolean {
  if (typeof window === "undefined") return false;
  if (window.isSecureContext) return true;
  return window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1";
}

/**
 * The best MediaRecorder mime type this browser admits to supporting.
 * Returns null when nothing matches — pass no mimeType to MediaRecorder and let
 * it choose, then read `recorder.mimeType` back.
 */
export function preferredAudioMime(): string | null {
  if (!supportsMediaRecorder()) return null;
  const isTypeSupported = window.MediaRecorder.isTypeSupported;
  if (typeof isTypeSupported !== "function") return null;

  for (const candidate of AUDIO_MIME_CANDIDATES) {
    try {
      if (isTypeSupported(candidate)) return candidate;
    } catch {
      /* Some engines throw on malformed types instead of returning false. */
    }
  }
  return null;
}

/** Container extension for a recorder mime type, for naming uploaded blobs. */
export function extensionForAudioMime(mimeType: string): string {
  const base = mimeType.split(";")[0].trim().toLowerCase();
  if (base === "audio/mp4") return ".m4a";
  if (base === "audio/ogg") return ".ogg";
  if (base === "audio/wav" || base === "audio/wave") return ".wav";
  return ".webm";
}

// --- Readiness --------------------------------------------------------------

export type ReadinessCode =
  | "insecure-context"
  | "no-getusermedia"
  | "no-mediarecorder"
  | "no-web-audio"
  | "no-audio-codec"
  | "no-frame-callback"
  | "audio-locked";

export interface ReadinessIssue {
  code: ReadinessCode;
  /** Written for a player standing in a living room, not a developer. */
  message: string;
  /** Fatal issues mean this device cannot record at all. */
  fatal: boolean;
}

export interface MediaCapabilities {
  secureContext: boolean;
  getUserMedia: boolean;
  mediaRecorder: boolean;
  webAudio: boolean;
  audioMimeType: string | null;
  videoFrameCallback: boolean;
  /** State of the shared AudioContext. "uninitialized" before the first gesture. */
  audioState: AudioLifecycleState;
  iOS: boolean;
  android: boolean;
  safari: boolean;
  mobile: boolean;
}

export interface MediaReadiness {
  /** No fatal issues — this device can play the game. */
  ok: boolean;
  issues: ReadinessIssue[];
  capabilities: MediaCapabilities;
}

/**
 * Gate the recording UI on this. `ok === false` means show the issue messages
 * and keep the player out of the recording phase rather than letting them
 * discover the problem three seconds into their dub.
 */
export function mediaReadiness(): MediaReadiness {
  const capabilities: MediaCapabilities = {
    secureContext: isSecureContextAvailable(),
    getUserMedia: supportsGetUserMedia(),
    mediaRecorder: supportsMediaRecorder(),
    webAudio: isAudioSupported(),
    audioMimeType: preferredAudioMime(),
    audioState: audioState(),
    videoFrameCallback: supportsVideoFrameCallback(),
    iOS: isIOS(),
    android: isAndroid(),
    safari: isSafari(),
    mobile: isMobile(),
  };

  const issues: ReadinessIssue[] = [];

  if (!capabilities.secureContext) {
    issues.push({
      code: "insecure-context",
      message: "Microphones only work over HTTPS. Open the game at its https:// address.",
      fatal: true,
    });
  }

  if (!capabilities.getUserMedia) {
    issues.push({
      code: "no-getusermedia",
      message: capabilities.iOS
        ? "This browser can't reach the microphone. On iPhone and iPad, open the game in Safari."
        : "This browser can't reach the microphone. Try the latest Chrome, Safari or Firefox.",
      fatal: true,
    });
  }

  if (!capabilities.mediaRecorder) {
    issues.push({
      code: "no-mediarecorder",
      message: capabilities.iOS
        ? "Recording needs iOS 14.5 or newer in Safari. Update iOS, or use another device."
        : "This browser can't record audio. Try the latest Chrome, Safari or Firefox.",
      fatal: true,
    });
  }

  if (!capabilities.webAudio) {
    issues.push({
      code: "no-web-audio",
      message: "This browser has no Web Audio support, so dubs can't be played back.",
      fatal: true,
    });
  }

  if (capabilities.mediaRecorder && capabilities.audioMimeType === null) {
    issues.push({
      code: "no-audio-codec",
      message:
        "No recording format this browser and the game both understand. Recording will use the browser default and may not play back everywhere.",
      fatal: false,
    });
  }

  // Only once a context exists: "uninitialized" is the normal state before the
  // first gesture and is not a problem worth showing anybody.
  if (capabilities.audioState === "suspended" || capabilities.audioState === "closed") {
    issues.push({
      code: "audio-locked",
      message: "Sound is locked on this device. Tap anywhere to enable audio.",
      fatal: false,
    });
  }

  if (!capabilities.videoFrameCallback) {
    issues.push({
      code: "no-frame-callback",
      message: "This browser can't report video frames precisely, so timing may drift slightly.",
      fatal: false,
    });
  }

  return {
    ok: issues.every((issue) => !issue.fatal),
    issues,
    capabilities,
  };
}

/**
 * Current microphone permission, when the browser will tell us.
 * Safari does not implement the microphone permission name and throws, hence
 * the "unknown" arm — treat it as "we'll find out when we ask".
 */
export async function micPermissionState(): Promise<PermissionState | "unknown"> {
  if (typeof navigator === "undefined" || !navigator.permissions?.query) return "unknown";
  try {
    const status = await navigator.permissions.query({ name: "microphone" as PermissionName });
    return status.state;
  } catch {
    return "unknown";
  }
}
